import {
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Select,
  Collapse,
  Flex,
  NumberInputField,
  NumberInput,
  NumberInputStepper,
  NumberDecrementStepper,
  NumberIncrementStepper,
  Text,
  CheckboxGroup,
  Checkbox,
} from '@chakra-ui/react';
import { useState, useRef, useEffect, useCallback } from 'react';
import SectionWrapper from './SectionWrapper';
import { useAppSelector, useAppDispatch } from '../../../store/redux/hooks';
import {
  setSimpleField,
  setTemplate,
  setTemplateType,
  setFieldProp,
  clearStampBannerImages,
  clearStampImagesAndSetBannerToPlaceholder,
} from '../../../store/redux/slices/template.slice';
import {
  clearStampBannerImageErrors,
  clearStampAttributeErrors,
  clearStampCountRelatedErrorsAboveCount,
  clearNamespaceErrors,
} from '../../../store/redux/slices/templateErrors.slice';
import {
  StampFeedbackType,
  TemplateState,
  TemplateType,
} from '../../../store/types/template';
import { useTemplateFieldValidation } from './validation/useTemplateFieldValidation';
import { LabelWithTooltip } from './LabelWithTooltip';
import TemplateTypeChangeWarningModal from './modals/TemplateTypeChangeWarningModal';
import { buildInitialFields } from './utils/fieldHelpers';
import { BALANCE_FIELD } from './utils/fieldVisibilityConfig';
import {
  APPLE_ENHANCED_DESIGN_FEATURED_ACTIONS_NAMESPACE,
  APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE,
} from './utils/appleEnhancedDesignConstants';
import { shouldClearEnhancedDesignImageErrors } from './utils/enhancedDesignEligibility';
import {
  NEW_LOGO_BASE64,
  NEW_ICON_BASE64,
  NEW_BANNER_BASE64,
  NEW_EVENT_TICKET_BANNER_BASE64,
  GOOGLE_DEFAULT_WAVE_BANNER_BASE64,
} from '../../../utils/base64';
import FieldEditIndicator from './FieldEditIndicator';
import { useFieldEditIndicator } from './hooks/useFieldEditIndicator';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';
import { useTemplateNameValidation } from './hooks/useTemplateNameValidation';
import CharLimitWarning from './CharLimitWarning';
import { MeContext } from '../../../context/MeContext';
import { useContext } from 'react';
import { MeContextState, Entity } from '../../../store/types/account';
import { CustomChevronLeftIcon } from '../../../theme/icons/CustomChevronLeftIcon';

const TemplateDetails = ({
  watermarkEnabled,
  isEdit,
}: {
  isEdit: boolean;
  watermarkEnabled: boolean;
}) => {
  const templateName = useAppSelector((s) => s.template.templateName);
  const templateUuid = useAppSelector((s) => s.template.uuid);
  const fields = useAppSelector((s) => s.template.fields);
  const images = useAppSelector((s) => s.template.images);

  // Get current entity for entity-scoped validation
  const { state: meStateRaw } = useContext(MeContext) as {
    state: MeContextState;
  };
  const meState = meStateRaw;
  const expressivePassRolloutEnabled =
    (meState as Record<string, unknown>).expressivePassRolloutEnabled === true;
  const filterEntity: Entity | null =
    meState.entities?.length === 1
      ? meState.entities[0]
      : (meState.filterEntity ?? null);
  const currentEntityUuid = filterEntity?.uuid;
  const templateType = useAppSelector((s) => s.template.templateType);
  const appleEnhancedDesignType = useAppSelector(
    (s) => s.template.appleEnhancedDesign.type
  );
  const fromPreset = useAppSelector((s) => s.template._fromPreset);
  const description = useAppSelector((s) => s.template.description);
  const centralTitle = useAppSelector((s) => s.template.centralTitle);
  const transitType = useAppSelector((s) => s.template.transitType);
  const stampCount = useAppSelector((s) => s.template.stampCount);
  const stampFeedbackTypes = useAppSelector(
    (s) => s.template.stampFeedbackTypes
  );
  const sectionTitle =
    templateType === 'stampCard' ? 'Stamp card set up' : 'Template Details';

  const templateNameRef = useRef<HTMLDivElement>(null);
  const templateTypeRef = useRef<HTMLDivElement>(null);
  const transitTypeRef = useRef<HTMLDivElement>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const centralTitleRef = useRef<HTMLDivElement>(null);
  const notificationHeaderRef = useRef<HTMLDivElement>(null);
  const stampCardTypeRef = useRef<HTMLDivElement>(null);
  const stampFeedbackTypeRef = useRef<HTMLDivElement>(null);

  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: {
      templateName: templateNameRef,
      templateType: templateTypeRef,
      transitType: transitTypeRef,
      description: descriptionRef,
      centralTitle: centralTitleRef,
      notificationHeader: notificationHeaderRef,
      stampCount: stampCardTypeRef,
      stampFeedbackType: stampFeedbackTypeRef,
    },
  });

  const dispatch = useAppDispatch();

  const templateTypes = [
    {
      label: 'Generic pass',
      value: 'generic',
    },
    {
      label: 'Loyalty pass',
      value: 'loyalty',
    },
    {
      label: 'Event ticket',
      value: 'eventTicket',
    },
    {
      label: 'Transit',
      value: 'transit',
    },
    {
      label: 'Loyalty Stamp Card',
      value: 'stampCard',
    },
  ];

  const { validateField, handleFocus, handleBlur } =
    useTemplateFieldValidation();
  const errors = useAppSelector((s) => s.templateErrors);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNewType, setPendingNewType] = useState<TemplateType | null>(
    null
  );
  const selectRef = useRef<HTMLSelectElement>(null);

  const { isTemplateNameUnique } = useTemplateNameValidation(
    isEdit,
    templateUuid,
    currentEntityUuid
  );

  const handleChange = (
    field: keyof TemplateState,
    value: string | number | StampFeedbackType[]
  ) => {
    dispatch(setSimpleField({ field, value }));
    validateField(field, value, { isTemplateNameUnique });
  };

  // Local state for template name to avoid Redux dispatch on every keystroke
  const [localTemplateName, setLocalTemplateName] = useState(templateName);
  const templateNameTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    setLocalTemplateName(templateName);
  }, [templateName]);
  const handleTemplateNameChange = useCallback(
    (value: string) => {
      setLocalTemplateName(value);
      clearTimeout(templateNameTimerRef.current);
      templateNameTimerRef.current = setTimeout(() => {
        handleChange('templateName', value);
      }, 300);
    },
    [handleChange]
  );
  useEffect(() => {
    return () => clearTimeout(templateNameTimerRef.current);
  }, []);

  const hasUserData = () => {
    // Template name, description, central title and notification header
    // are preserved across type changes, so don't count them.

    // Check if any images differ from defaults
    const hasCustomImages =
      !!(
        images.apple.logo ||
        images.apple.icon ||
        images.apple.banner ||
        images.apple.thumbnail
      ) ||
      !!(
        images.google.logo ||
        images.google.wideLogo ||
        (images.google.banner &&
          images.google.banner !== GOOGLE_DEFAULT_WAVE_BANNER_BASE64)
      ) ||
      (images.shared.logo && images.shared.logo !== NEW_LOGO_BASE64) ||
      (images.shared.icon && images.shared.icon !== NEW_ICON_BASE64) ||
      (images.shared.banner &&
        images.shared.banner !== NEW_BANNER_BASE64 &&
        images.shared.banner !== NEW_EVENT_TICKET_BANNER_BASE64);
    if (hasCustomImages) return true;

    // Compare fields against defaults to detect user edits.
    // A field is "default" if it matches the defaults of ANY template type,
    // since switching types resets fields to that type's defaults.
    const allDefaultLabels = new Set<string>();
    const allDefaultValues = new Set<string>(['']);
    const typeKeys: Array<
      keyof typeof import('./utils/fieldVisibilityConfig').FIELD_CONFIG
    > = ['loyalty', 'generic', 'eventTicket', 'transit', 'stampCard'];
    typeKeys.forEach((t) => {
      const df = buildInitialFields(t);
      Object.values(df).forEach((slots: any) =>
        Object.values(slots).forEach((f: any) => {
          if (f?.label) allDefaultLabels.add(f.label);
          if (f?.defaultValue != null)
            allDefaultValues.add(String(f.defaultValue));
        })
      );
    });
    allDefaultLabels.add(BALANCE_FIELD.label);

    return Object.entries(fields).some(([section, slots]) =>
      Object.entries(slots as Record<string, any>).some(
        ([slot, f]: [string, any]) => {
          if (!allDefaultLabels.has(f.label) && f.label) return true;
          const dv = String(f.defaultValue ?? '');
          if (!allDefaultValues.has(dv)) return true;
          return false;
        }
      )
    );
  };

  const clearErrorsForTypeChange = (nextType: TemplateType) => {
    if (nextType !== 'stampCard') {
      dispatch(clearStampAttributeErrors());
    }
    if (
      shouldClearEnhancedDesignImageErrors(appleEnhancedDesignType, nextType)
    ) {
      dispatch(clearNamespaceErrors(APPLE_ENHANCED_DESIGN_IMAGES_NAMESPACE));
      dispatch(
        clearNamespaceErrors(APPLE_ENHANCED_DESIGN_FEATURED_ACTIONS_NAMESPACE)
      );
    }
  };

  const handleTemplateTypeChange = (newType: TemplateType) => {
    if (newType === templateType) {
      return;
    }
    if (!hasUserData()) {
      dispatch(setTemplate({ fields: buildInitialFields(newType) }));
      dispatch(
        setTemplateType({
          newType,
          watermarkEnabled: watermarkEnabled,
          autoToggleLoyaltyBalance: true,
        })
      );
      clearErrorsForTypeChange(newType);
      return;
    }
    setPendingNewType(newType);
    setIsModalOpen(true);
  };

  const handleConfirmTypeChange = () => {
    if (pendingNewType) {
      dispatch(
        setTemplateType({
          newType: pendingNewType,
          watermarkEnabled: watermarkEnabled,
          autoToggleLoyaltyBalance: true,
        })
      );
      clearErrorsForTypeChange(pendingNewType);
      setPendingNewType(null);
      setIsModalOpen(false);
    }
  };

  const handleCancelTypeChange = () => {
    if (selectRef.current) {
      selectRef.current.value = templateType;
    }
    setPendingNewType(null);
    setIsModalOpen(false);
  };

  const handleStampFeedbackTypeChange = (value: StampFeedbackType) => {
    const current = stampFeedbackTypes ?? [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    dispatch(setSimpleField({ field: 'stampFeedbackTypes', value: next }));
    if (value === 'changePassBanner' && !next.includes('changePassBanner')) {
      dispatch(clearStampBannerImages());
      dispatch(clearStampBannerImageErrors());
    }
    if (value === 'changePassBanner' && next.includes('changePassBanner')) {
      dispatch(clearStampImagesAndSetBannerToPlaceholder());
      dispatch(clearStampBannerImageErrors());
    }
  };

  return (
    <SectionWrapper
      title={sectionTitle}
      openOnLoad={true}
      id="template-details-section"
    >
      <FieldEditIndicator
        fieldRef={templateNameRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-40px"
        top="53px"
      >
        <FormControl isInvalid={!!errors.templateName} isRequired>
          <FormLabel variant="templateBuilderLabel">Template name</FormLabel>
          <Input
            value={localTemplateName}
            onChange={(e) => handleTemplateNameChange(e.target.value)}
            placeholder="Internal name of your template"
            onFocus={() => handleFocus('templateName')}
          />
          <FormErrorMessage pl={2} display="flex" alignItems="center" gap={1.5}>
            <CustomErrorIcon />
            {errors.templateName}
          </FormErrorMessage>
        </FormControl>
      </FieldEditIndicator>
      <FieldEditIndicator
        fieldRef={templateTypeRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-40px"
        top="60px"
      >
        <FormControl
          mt={4}
          isInvalid={!!errors.templateType}
          isRequired={!fromPreset && !isEdit}
        >
          <LabelWithTooltip
            label="Pass layout"
            tooltip="Select the layout for your pass. This defines how many fields you can add and which asset types are available."
          />
          {fromPreset || isEdit ? (
            <Text
              textStyle="bodyRegular"
              fontSize="16px"
              color="#989898"
              mt="6px"
            >
              {templateTypes.find((t) => t.value === templateType)?.label ??
                templateType}
            </Text>
          ) : (
            <Select
              ref={selectRef}
              value={templateType}
              onChange={(e) =>
                handleTemplateTypeChange(e.target.value as TemplateType)
              }
              onFocus={() => handleFocus('templateType')}
            >
              {templateTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Select>
          )}
          <FormErrorMessage pl={2} display="flex" alignItems="center" gap={1.5}>
            <CustomErrorIcon />
            {errors.templateType}
          </FormErrorMessage>
        </FormControl>
      </FieldEditIndicator>
      {pendingNewType && (
        <TemplateTypeChangeWarningModal
          isOpen={isModalOpen}
          onClose={handleCancelTypeChange}
          onConfirm={handleConfirmTypeChange}
          currentType={templateType}
          newType={pendingNewType}
        />
      )}

      <Collapse in={templateType === 'transit'}>
        <FieldEditIndicator
          fieldRef={transitTypeRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="60px"
        >
          <FormControl mt={4} isInvalid={!!errors.transitType} isRequired>
            <LabelWithTooltip
              label="Transit Type"
              tooltip="Choose the transport mode for this pass. This controls the icon shown on the pass."
            />
            <Select
              value={transitType}
              onChange={(e) => handleChange('transitType', e.target.value)}
              onFocus={() => handleFocus('transitType')}
            >
              <option value="airplane">Airplane</option>
              <option value="bus">Bus</option>
              <option value="train">Train</option>
              <option value="ferry">Ferry</option>
              <option value="other">Other</option>
            </Select>
            <FormErrorMessage
              pl={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {errors.transitType}
            </FormErrorMessage>
          </FormControl>
        </FieldEditIndicator>
      </Collapse>

      <Collapse in={templateType === 'stampCard'}>
        <FieldEditIndicator
          fieldRef={stampCardTypeRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="55px"
        >
          <FormControl mt={4} isRequired>
            <FormLabel variant="templateBuilderLabel">
              Number of stamps required
            </FormLabel>

            <NumberInput
              value={stampCount || 2}
              onChange={(value) => {
                const num = Math.min(Math.max(Number(value) || 2, 2), 12);
                dispatch(setSimpleField({ field: 'stampCount', value: num }));
                dispatch(clearStampCountRelatedErrorsAboveCount(num + 1));
              }}
              onFocus={() => handleFocus('stampCount')}
              min={2}
              max={12}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper
                  children={
                    <CustomChevronLeftIcon
                      transform="rotate(90deg)"
                      w="18px"
                      h="18px"
                      color="primaryDark"
                    />
                  }
                />
                <NumberDecrementStepper
                  children={
                    <CustomChevronLeftIcon
                      transform="rotate(270deg)"
                      w="18px"
                      h="18px"
                      color="primaryDark"
                    />
                  }
                />
              </NumberInputStepper>
            </NumberInput>
          </FormControl>
        </FieldEditIndicator>
        <FieldEditIndicator
          fieldRef={stampFeedbackTypeRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="12px"
        >
          <FormControl mt={4} onClick={() => handleFocus('stampFeedbackType')}>
            <FormLabel variant="templateBuilderLabel">
              Stamp feedback type
            </FormLabel>
            <Text fontSize="12px" color="secondaryDark" mt={2}>
              <em>
                Choose how customers are notified when a stamp is earned. You
                can select more than one option.
              </em>
            </Text>
            <Flex direction="column" gap={4} mt={4} mb={6} ml={4}>
              <Checkbox
                variant="primary"
                size="sm"
                value="changePassBanner"
                isChecked={stampFeedbackTypes?.includes('changePassBanner')}
                onChange={(e) =>
                  handleStampFeedbackTypeChange('changePassBanner')
                }
                onFocus={() => handleFocus('stampFeedbackType')}
              >
                Change pass banner
              </Checkbox>
              <Checkbox
                variant="primary"
                size="sm"
                value="sendPushNotification"
                isChecked={stampFeedbackTypes?.includes('sendPushNotification')}
                onChange={(e) =>
                  handleStampFeedbackTypeChange('sendPushNotification')
                }
                onFocus={() => handleFocus('stampFeedbackType')}
              >
                Send push notification
              </Checkbox>
              <Checkbox
                variant="primary"
                size="sm"
                value="updatePassText"
                isChecked={stampFeedbackTypes?.includes('updatePassText')}
                onChange={(e) =>
                  handleStampFeedbackTypeChange('updatePassText')
                }
                onFocus={() => handleFocus('stampFeedbackType')}
              >
                Update pass text
              </Checkbox>
            </Flex>
          </FormControl>
        </FieldEditIndicator>
      </Collapse>

      <FieldEditIndicator
        fieldRef={descriptionRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-40px"
        top="60px"
      >
        <FormControl mt={4} isInvalid={!!errors.description} isRequired>
          <LabelWithTooltip
            label="Description"
            tooltip="This text appears as the main title on Google Wallet passes, serves as the geofence alert header on Google Wallet, and is shown on the back of Apple Wallet passes. Keep it short for the best visual fit."
          />
          <Input
            value={description}
            placeholder="e.g. PassEntry Rewards, PassEntry Membership"
            onChange={(e) => handleChange('description', e.target.value)}
            onFocus={() => handleFocus('description')}
          />
          <FormErrorMessage pl={2} display="flex" alignItems="center" gap={1.5}>
            <CustomErrorIcon />
            {errors.description}
          </FormErrorMessage>
        </FormControl>
      </FieldEditIndicator>

      {templateType !== 'generic' && (
        <FieldEditIndicator
          fieldRef={centralTitleRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-40px"
          top="60px"
        >
          <FormControl mt={4} isInvalid={!!errors.centralTitle} isRequired>
            <LabelWithTooltip
              label="Google pass title"
              tooltip="The main title shown on Google Wallet passes. Helps users quickly understand what the pass is for."
            />
            <Input
              value={centralTitle}
              placeholder="e.g. Loyalty Card, Member Pass"
              onChange={(e) => handleChange('centralTitle', e.target.value)}
              onFocus={() => handleFocus('centralTitle')}
              onBlur={handleBlur}
            />
            <FormErrorMessage
              pl={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {errors.centralTitle}
            </FormErrorMessage>
            {expressivePassRolloutEnabled && (
              <CharLimitWarning value={centralTitle} limit={47} />
            )}
          </FormControl>
        </FieldEditIndicator>
      )}
    </SectionWrapper>
  );
};

export default TemplateDetails;
